/**
 * Resolve broadcast channel + Remind Me EPG for a sports nextEvent.
 *
 * Channel badge: only from `nextEvent.broadcastChannels` (never invent from /events).
 * Remind bell: only when a broadcast entry is an EPG-linked programme (exact hub match),
 * resolved via programme id / title+time against search results (for dbId), never fuzzy team titles.
 */

function pickChannelFields(entry) {
  if (!entry || typeof entry !== 'object') return null;

  const name =
    entry.name ||
    entry.channelName ||
    entry.label ||
    (typeof entry.channel === 'string' ? entry.channel : null) ||
    null;

  const logo =
    entry.logoUrl ||
    entry.logo ||
    entry.channelLogo ||
    entry.channelLogoUrl ||
    entry.imageUrl ||
    null;

  if (!name && !logo) return null;
  return { name, logo };
}

function normalize(text) {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function parseTime(value) {
  if (!value) return NaN;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : NaN;
}

function epgIdFromChannel(entry) {
  if (!entry || typeof entry !== 'object') return null;
  return (
    entry.programmeId ||
    entry.epgEventId ||
    entry.epgProgrammeId ||
    entry.dbId ||
    null
  );
}

/** Real EPG programme for this fixture — not SportsDB text stubs or series fallbacks. */
function isEpgLinkedBroadcast(entry) {
  if (!entry || typeof entry !== 'object') return false;
  if (entry.inferredFromRecent) return false;
  if (entry.matchSource === 'epg_series_fallback') return false;
  if (entry.matchSource === 'epg_exact') return true;
  return Boolean(
    epgIdFromChannel(entry) ||
      (entry.programmeTitle && entry.startsAt)
  );
}

function findEpgInResults(entry, results) {
  if (!entry || !Array.isArray(results) || !results.length) return null;

  const epgId = epgIdFromChannel(entry);
  if (epgId) {
    const id = String(epgId);
    const byId = results.find((r) => {
      if (r?.platform && r.platform !== 'EPG') return false;
      return (
        String(r.dbId || '') === id ||
        String(r.id || '') === id ||
        String(r.programmeId || '') === id
      );
    });
    if (byId) return byId;
  }

  const title = normalize(entry.programmeTitle);
  const startMs = parseTime(entry.startsAt);
  const channelName = normalize(entry.name || entry.channelName);

  let best = null;
  let bestScore = 0;
  for (const item of results) {
    if (item?.platform && item.platform !== 'EPG') continue;
    let score = 0;
    const itemTitle = normalize(item.title);
    if (title && itemTitle && title === itemTitle) score += 5;
    else if (title && itemTitle && (itemTitle.includes(title) || title.includes(itemTitle))) {
      score += 2;
    }

    const itemStart = parseTime(item.startsAt || item.start);
    if (Number.isFinite(startMs) && Number.isFinite(itemStart)) {
      const hours = Math.abs(startMs - itemStart) / 36e5;
      if (hours <= 0.25) score += 4;
      else if (hours <= 1) score += 2;
      else if (hours <= 3) score += 1;
      else continue;
    }

    const itemChannel = normalize(item.channelName || item.channel || item.meta?.channel);
    if (channelName && itemChannel && channelName === itemChannel) score += 2;

    if (score > bestScore) {
      bestScore = score;
      best = item;
    }
  }

  // Require a strong join (title+time or id path above) — not team-name fuzzy.
  if (!best || bestScore < 6) return null;
  return best;
}

/**
 * @param {object|null} nextEvent
 * @param {object[]} [results]
 * @returns {{ name: string|null, logo: string|null }|null}
 */
export function resolveNextEventChannel(nextEvent) {
  const channels = nextEvent?.broadcastChannels;
  if (!Array.isArray(channels) || channels.length === 0) return null;

  for (const entry of channels) {
    const fields = pickChannelFields(entry);
    if (fields) return fields;
  }

  return null;
}

/**
 * EPG event for Remind Me — only when hub already linked an EPG programme.
 * Prefers a matching /events card (has dbId for subscriptions).
 *
 * @param {object|null} nextEvent
 * @param {object[]} [results]
 * @returns {object|null}
 */
export function resolveNextEventRemindEpg(nextEvent, results) {
  const channels = nextEvent?.broadcastChannels;
  if (!Array.isArray(channels) || channels.length === 0) return null;

  for (const entry of channels) {
    if (!isEpgLinkedBroadcast(entry)) continue;
    const matched = findEpgInResults(entry, results);
    if (matched && (matched.dbId || matched.id)) return matched;
  }

  return null;
}
